import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type { Egzemplarz, Indeks, Lista } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import { Karta, Komunikat, Ladowanie, Plakietka, Przelacznik, Przycisk, Pusto, Wybor } from '../components/ui';
import { data as fData, dataGodzina, ilosc, zlote } from '../lib/format';
import { STANY } from '../lib/slowniki';

const RODZAJE: Record<string, string> = {
  BUDYNEK: 'Budynek', PIETRO: 'Piętro', SALA: 'Sala', STREFA: 'Strefa', SZAFA: 'Szafa', POLKA: 'Półka',
};

interface Miejsce { id: number; code: string; name: string; path: string | null }

interface Zawartosc {
  lokalizacja: {
    id: number; code: string; name: string; kind: string; path: string | null; isStorage: boolean;
    isClassroom: boolean; isActive: boolean; notes: string | null;
  };
  sciezka: { id: number; code: string; name: string }[];
  zPodlokalizacjami: boolean;
  podlokalizacje: { id: number; code: string; name: string; kind: string; isActive: boolean; egzemplarzy: number; pozycji: number }[];
  sprzet: {
    id: number; nrCsm: string; model: string | null; condition: keyof typeof STANY;
    item: { id: number; code: string; name: string }; wSerwisie: boolean; lokalizacja: Miejsce | null;
  }[] | null;
  materialy: {
    item: { id: number; code: string; name: string; unit: string; isDrugDummy: boolean; minQty: string | null };
    ilosc: string; wartosc: string | null;
    partie: { lotNumber: string | null; expiryDate: string | null; ilosc: string; lokalizacja: Miejsce | null }[];
  }[] | null;
  przypisaneBezStanu: { id: number; code: string; name: string; unit: string; lokalizacja: Miejsce | null }[] | null;
  zajecia: {
    id: number; startsAt: string; endsAt: string; groupName: string | null; instructorName: string;
    course: { code: string; name: string };
  }[] | null;
  podsumowanie: {
    egzemplarzy: number | null; pozycjiMaterialow: number | null; sztukMaterialow: string | null; wartosc: string | null;
  };
}

/**
 * „Wszystko o tym miejscu”: sprzęt, materiały ze stanem, przypisania i zajęcia — na żywo z tych
 * samych danych, które zapisują formularze, więc przypisanie czegokolwiek do sali widać tu od razu.
 */
export function LokalizacjaSzczegoly() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const { maUprawnienie } = useAuth();
  const [zPodlokalizacjami, setZPodlokalizacjami] = useState(true);

  const zapytanie = useQuery({
    queryKey: ['lokalizacja', id, zPodlokalizacjami],
    queryFn: () => api.get<Zawartosc>(`/lokalizacje/${id}/zawartosc?zPodlokalizacjami=${zPodlokalizacjami}`),
    // Kilka osób pracuje naraz — zawartość ma być aktualna bez przeładowywania strony.
    refetchInterval: 15_000,
  });

  if (zapytanie.isLoading) return <Ladowanie />;
  if (zapytanie.isError || !zapytanie.data) {
    const brak = zapytanie.error instanceof BladApi && zapytanie.error.status === 404;
    return (
      <div className="flex flex-col gap-4">
        <Komunikat rodzaj="blad">{brak ? 'Nie ma takiej lokalizacji.' : 'Nie udało się wczytać lokalizacji. Odśwież stronę.'}</Komunikat>
        <div><Link to="/lokalizacje" className="text-sm text-bordo-600 underline">Wróć do listy lokalizacji</Link></div>
      </div>
    );
  }

  const z = zapytanie.data;
  const l = z.lokalizacja;

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Ścieżka" className="flex flex-wrap items-center gap-1.5 text-sm text-kamien-500">
        <Link to="/lokalizacje" className="hover:text-bordo-600">Lokalizacje</Link>
        {z.sciezka.map((p) => (
          <span key={p.id} className="flex items-center gap-1.5">
            <span aria-hidden="true">›</span>
            <Link to={`/lokalizacje/${p.id}`} className="hover:text-bordo-600">{p.name}</Link>
          </span>
        ))}
        <span aria-hidden="true">›</span>
        <span className="text-kamien-800">{l.name}</span>
      </nav>

      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-kamien-900">{l.name}</h1>
          <span className="font-mono text-xs text-kamien-500">{l.code}</span>
          <Plakietka>{RODZAJE[l.kind] ?? l.kind}</Plakietka>
          {l.isClassroom && <Plakietka ton="ok">sala zajęciowa</Plakietka>}
          {!l.isActive && <Plakietka ton="blad">wyłączona</Plakietka>}
        </div>
        {l.path && <p className="mt-1 text-sm text-kamien-500">{l.path}</p>}
        {l.notes && <p className="mt-2 max-w-3xl text-sm text-kamien-700">{l.notes}</p>}
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-kamien-200 ring-1 ring-kamien-200 sm:grid-cols-4">
        {z.podsumowanie.egzemplarzy !== null && <Licznik etykieta="Egzemplarzy sprzętu" wartosc={String(z.podsumowanie.egzemplarzy)} />}
        {z.podsumowanie.pozycjiMaterialow !== null && <Licznik etykieta="Pozycji materiałów" wartosc={String(z.podsumowanie.pozycjiMaterialow)} />}
        {z.podsumowanie.sztukMaterialow !== null && <Licznik etykieta="Sztuk materiałów" wartosc={ilosc(z.podsumowanie.sztukMaterialow)} />}
        {z.podsumowanie.wartosc !== null && <Licznik etykieta="Wartość materiałów" wartosc={zlote(z.podsumowanie.wartosc)} />}
      </dl>

      {(z.podlokalizacje.length > 0 || !zPodlokalizacjami) && (
        <Przelacznik
          etykieta="Razem z podlokalizacjami"
          opis="Wyłącz, żeby zobaczyć tylko to, co leży bezpośrednio tutaj, a nie w szafach i na półkach"
          checked={zPodlokalizacjami}
          onChange={(e) => setZPodlokalizacjami(e.target.checked)}
        />
      )}

      {z.podlokalizacje.length > 0 && (
        <Karta tytul="Podlokalizacje" opis="Kliknij, żeby zobaczyć, co jest w środku">
          <ul className="divide-y divide-kamien-200">
            {z.podlokalizacje.map((p) => (
              <li key={p.id}>
                <Link to={`/lokalizacje/${p.id}`}
                  className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-kamien-50">
                  <span className="font-mono text-xs text-kamien-500">{p.code}</span>
                  <span className={`flex-1 text-sm ${p.isActive ? 'text-kamien-900' : 'text-kamien-400'}`}>{p.name}</span>
                  <span className="text-xs text-kamien-400">{RODZAJE[p.kind] ?? p.kind}</span>
                  {p.egzemplarzy > 0 && <Plakietka>{p.egzemplarzy} egz.</Plakietka>}
                  {p.pozycji > 0 && <Plakietka>{p.pozycji} poz. materiałów</Plakietka>}
                </Link>
              </li>
            ))}
          </ul>
        </Karta>
      )}

      {z.sprzet && (
        <Sprzet lokalizacjaId={id} z={z} mozeZapisac={maUprawnienie('sprzet.zapis') && l.isActive} />
      )}
      {z.materialy && z.przypisaneBezStanu && (
        <Materialy lokalizacjaId={id} z={z} mozeZapisac={maUprawnienie('kartoteka.zapis') && l.isActive} />
      )}

      {z.zajecia && (
        <Karta tytul="Najbliższe zajęcia w tej sali">
          {z.zajecia.length > 0 ? (
            <ul className="divide-y divide-kamien-200">
              {z.zajecia.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                  <span className="w-40 text-kamien-700">{dataGodzina(c.startsAt)}</span>
                  <span className="flex-1 text-kamien-900">{c.course.name}</span>
                  <span className="text-kamien-500">{c.groupName ? `grupa ${c.groupName} · ` : ''}{c.instructorName}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-6 text-sm text-kamien-500">Brak zaplanowanych zajęć w tej sali.</p>
          )}
        </Karta>
      )}
    </div>
  );
}

function Licznik({ etykieta, wartosc }: { etykieta: string; wartosc: string }) {
  return (
    <div className="bg-white px-4 py-3">
      <dt className="text-xs uppercase tracking-wide text-kamien-500">{etykieta}</dt>
      <dd className="mt-1 text-lg font-semibold text-kamien-900">{wartosc}</dd>
    </div>
  );
}

/** Po każdej zmianie przypisania odświeżamy wszystko, co ją pokazuje. */
function useOdswiez() {
  const qc = useQueryClient();
  return () => {
    for (const klucz of ['lokalizacja', 'lokalizacje', 'sprzet', 'kartoteka', 'stany']) {
      void qc.invalidateQueries({ queryKey: [klucz] });
    }
  };
}

function Sprzet({ lokalizacjaId, z, mozeZapisac }: { lokalizacjaId: number; z: Zawartosc; mozeZapisac: boolean }) {
  const odswiez = useOdswiez();
  const [wybrany, setWybrany] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const sprzet = z.sprzet!;

  const wszystkie = useLista({
    queryKey: ['sprzet', 'do-przypisania'],
    queryFn: () => api.get<Lista<Egzemplarz>>('/sprzet?naStronie=500'),
    enabled: mozeZapisac,
  });
  const obecne = new Set(sprzet.map((e) => e.id));
  const kandydaci = wszystkie.pozycje.filter((e) => !obecne.has(e.id) && e.condition !== 'WYCOFANY');

  const przypisz = useMutation({
    mutationFn: (p: { id: number; version: number; locationId: number | null }) =>
      api.put(`/sprzet/${p.id}/lokalizacja`, { locationId: p.locationId, version: p.version }),
    onSuccess: () => { setBlad(null); setWybrany(''); odswiez(); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać.'),
  });

  return (
    <Karta tytul="Wyposażenie — sprzęt" opis="Egzemplarze, których miejscem jest ta lokalizacja">
      {mozeZapisac && (
        <form className="flex flex-wrap items-end gap-3 border-b border-kamien-200 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            const egz = kandydaci.find((x) => String(x.id) === wybrany);
            if (egz) przypisz.mutate({ id: egz.id, version: egz.version, locationId: lokalizacjaId });
          }}>
          <div className="min-w-64 flex-1">
            <Wybor etykieta="Przypisz sprzęt do tego miejsca" value={wybrany} onChange={(e) => setWybrany(e.target.value)}
              blad={wszystkie.blad}>
              <option value="">— wybierz egzemplarz —</option>
              {kandydaci.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nrCsm} — {e.item.name}{e.model ? ` (${e.model})` : ''}
                  {e.currentLocation ? ` · teraz: ${e.currentLocation.path ?? e.currentLocation.name}` : ' · bez miejsca'}
                </option>
              ))}
            </Wybor>
          </div>
          <Przycisk type="submit" wariant="glowny" disabled={!wybrany || przypisz.isPending}>Przypisz tutaj</Przycisk>
        </form>
      )}
      {blad && <div className="px-5 pt-4"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}

      {sprzet.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                <th className="px-5 py-3 font-medium">Nr CSM</th>
                <th className="px-5 py-3 font-medium">Sprzęt</th>
                <th className="px-5 py-3 font-medium">Model</th>
                <th className="px-5 py-3 font-medium">Stan</th>
                {z.zPodlokalizacjami && z.podlokalizacje.length > 0 && <th className="px-5 py-3 font-medium">Gdzie dokładnie</th>}
                {mozeZapisac && <th className="px-5 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-200">
              {sprzet.map((e) => (
                <tr key={e.id}>
                  <td className="px-5 py-3 font-mono text-xs text-kamien-900">{e.nrCsm}</td>
                  <td className="px-5 py-3 text-kamien-900">{e.item.name}</td>
                  <td className="px-5 py-3 text-kamien-600">{e.model ?? '—'}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Plakietka ton={STANY[e.condition].ton}>{STANY[e.condition].etykieta}</Plakietka>
                      {e.wSerwisie && <Plakietka ton="uwaga">w serwisie</Plakietka>}
                    </div>
                  </td>
                  {z.zPodlokalizacjami && z.podlokalizacje.length > 0 && (
                    <td className="px-5 py-3 text-xs text-kamien-600">
                      {e.lokalizacja && e.lokalizacja.id !== lokalizacjaId
                        ? <Link to={`/lokalizacje/${e.lokalizacja.id}`} className="text-bordo-600 hover:underline">{e.lokalizacja.name}</Link>
                        : 'bezpośrednio tutaj'}
                    </td>
                  )}
                  {mozeZapisac && (
                    <td className="px-5 py-3 text-right">
                      <ZdejmijSprzet id={e.id} nr={e.nrCsm} onDone={odswiez} onBlad={setBlad} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Pusto tytul="Brak sprzętu w tym miejscu"
          opis={mozeZapisac ? 'Wybierz egzemplarz powyżej, żeby go tu przypisać.' : 'Nic nie zostało tu przypisane.'} />
      )}
    </Karta>
  );
}

/** Wersję pobieramy tuż przed zapisem — lista w oknie mogła się zestarzeć. */
function ZdejmijSprzet({ id, nr, onDone, onBlad }: {
  id: number; nr: string; onDone: () => void; onBlad: (m: string | null) => void;
}) {
  const wyjmij = useMutation({
    mutationFn: async () => {
      const biezacy = await api.get<Egzemplarz>(`/sprzet/${id}`);
      return api.put(`/sprzet/${id}/lokalizacja`, { locationId: null, version: biezacy.version });
    },
    onSuccess: () => { onBlad(null); onDone(); },
    onError: (e) => onBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać.'),
  });
  return (
    <Przycisk type="button" wariant="cichy" className="min-h-9 px-2 text-xs" disabled={wyjmij.isPending}
      aria-label={`Zdejmij ${nr} z tej lokalizacji`} onClick={() => wyjmij.mutate()}>
      Zdejmij z miejsca
    </Przycisk>
  );
}

function Materialy({ lokalizacjaId, z, mozeZapisac }: { lokalizacjaId: number; z: Zawartosc; mozeZapisac: boolean }) {
  const odswiez = useOdswiez();
  const [wybrany, setWybrany] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const materialy = z.materialy!;
  const bezStanu = z.przypisaneBezStanu!;

  const wszystkie = useLista({
    queryKey: ['kartoteka', 'do-przypisania'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?type=MATERIAL&tylkoAktywne=true&naStronie=500'),
    enabled: mozeZapisac,
  });
  const kandydaci = wszystkie.pozycje;

  const przypisz = useMutation({
    mutationFn: async (p: { id: number; locationId: number | null }) => {
      const biezacy = await api.get<Indeks>(`/kartoteka/${p.id}`);
      return api.put(`/kartoteka/${p.id}/lokalizacja`, { locationId: p.locationId, version: biezacy.version });
    },
    onSuccess: () => { setBlad(null); setWybrany(''); odswiez(); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać.'),
  });

  return (
    <Karta tytul="Materiały" opis="Stan z dokumentów (PZ, WZ, MM…) oraz materiały przypisane do tego miejsca z góry">
      {mozeZapisac && (
        <form className="flex flex-wrap items-end gap-3 border-b border-kamien-200 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (wybrany) przypisz.mutate({ id: Number(wybrany), locationId: lokalizacjaId });
          }}>
          <div className="min-w-64 flex-1">
            <Wybor etykieta="Przypisz materiał do tego miejsca" value={wybrany} onChange={(e) => setWybrany(e.target.value)}
              blad={wszystkie.blad}>
              <option value="">— wybierz materiał —</option>
              {kandydaci.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.code} — {i.name}{i.defaultLocation ? ` · teraz: ${i.defaultLocation.path ?? i.defaultLocation.name}` : ''}
                </option>
              ))}
            </Wybor>
          </div>
          <Przycisk type="submit" wariant="glowny" disabled={!wybrany || przypisz.isPending}>Przypisz tutaj</Przycisk>
        </form>
      )}
      {blad && <div className="px-5 pt-4"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}

      {materialy.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                <th className="px-5 py-3 font-medium">Kod</th>
                <th className="px-5 py-3 font-medium">Materiał</th>
                <th className="px-5 py-3 text-right font-medium">Ilość</th>
                <th className="px-5 py-3 font-medium">Partie i miejsca</th>
                {z.podsumowanie.wartosc !== null && <th className="px-5 py-3 text-right font-medium">Wartość</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-200">
              {materialy.map((m) => (
                <tr key={m.item.id} className="align-top">
                  <td className="px-5 py-3 font-mono text-xs text-kamien-900">{m.item.code}</td>
                  <td className="px-5 py-3 text-kamien-900">{m.item.name}</td>
                  <td className="px-5 py-3 text-right text-kamien-900">{ilosc(m.ilosc)} {m.item.unit}</td>
                  <td className="px-5 py-3 text-xs text-kamien-600">
                    <ul className="flex flex-col gap-0.5">
                      {m.partie.map((p, i) => (
                        <li key={i}>
                          {ilosc(p.ilosc)} {m.item.unit}
                          {p.lotNumber ? ` · seria ${p.lotNumber}` : ''}
                          {p.expiryDate ? ` · ważne do ${fData(p.expiryDate)}` : ''}
                          {p.lokalizacja && p.lokalizacja.id !== lokalizacjaId && (
                            <> · <Link to={`/lokalizacje/${p.lokalizacja.id}`} className="text-bordo-600 hover:underline">{p.lokalizacja.name}</Link></>
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                  {z.podsumowanie.wartosc !== null && (
                    <td className="px-5 py-3 text-right text-kamien-700">{zlote(m.wartosc)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bezStanu.length > 0 && (
        <div className="border-t border-kamien-200 px-5 py-4">
          <p className="text-sm font-medium text-kamien-700">Przypisane do tego miejsca, ale bez stanu</p>
          <ul className="mt-2 divide-y divide-kamien-200">
            {bezStanu.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="font-mono text-xs text-kamien-500">{i.code}</span>
                <span className="flex-1 text-kamien-900">{i.name}</span>
                {i.lokalizacja && i.lokalizacja.id !== lokalizacjaId && (
                  <Link to={`/lokalizacje/${i.lokalizacja.id}`} className="text-xs text-bordo-600 hover:underline">{i.lokalizacja.name}</Link>
                )}
                {mozeZapisac && (
                  <Przycisk type="button" wariant="cichy" className="min-h-9 px-2 text-xs" disabled={przypisz.isPending}
                    onClick={() => przypisz.mutate({ id: i.id, locationId: null })}>
                    Odepnij
                  </Przycisk>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {materialy.length === 0 && bezStanu.length === 0 && (
        <Pusto tytul="Brak materiałów w tym miejscu"
          opis="Materiał pojawi się tu po przyjęciu na stan do tej lokalizacji albo po przypisaniu go powyżej." />
      )}
    </Karta>
  );
}
